import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppContextService } from '../../services/app-context.service';
import { User } from '../../models/User/user';
import { OperationResult } from '../../models/server-result/operation-result';
import { OperationError } from '../../models/server-result/operation-error';
import { LogService } from '../../services/log/log.service';
import { ToasterService } from '../../shared-components/custom-toaster/toaster.service';
import { HttpErrorResponse } from '@angular/common/http';
import { UtilitiesService } from '../../services/utils/utilities.service';
import { MissingAcceptedDocumentViewModel } from './view-models/missing-accepted-document-view-model';
import { LegalDocumentType } from '../../models/enums/documentation/legal-document-type';
import { TranslateService } from '../../services/translate/translate.service';
import { EifLoggedUser } from '../../models/User/eif-logged-user.type';
import { UserApiService } from '../../server-api/user-api/user-api.service';
import {
	AcceptanceOfMissingDocumentsRequest,
	AcceptMissingDocument,
	AcceptMissingDocumentLink,
	SearchedMissingDocument,
	SearchedMissingDocumentLink,
	SearchMissingAcceptanceOfDocumentsResult,
} from '../../server-api/user-api/user-api.models';

@Component({
	selector: 'app-missing-accepted',
	templateUrl: './missing-accepted.component.html',
	styleUrls: ['./missing-accepted.component.scss'],
	standalone: false,
})
export class MissingAcceptedComponent implements OnInit {
	public loggedUser: User | null = null;
	public isPerformingRequest = false;
	public requestErrors: OperationError[] = [];
	public missingAcceptanceDocuments: MissingAcceptedDocumentViewModel[] = [];
	public allDocsAccepted: boolean = false;

	constructor(
		private router: Router,
		private appContext: AppContextService,
		private userApiService: UserApiService,
		private logger: LogService,
		private toasterService: ToasterService,
		private utilitiesService: UtilitiesService,
		private translateService: TranslateService
	) {}

	ngOnInit(): void {
		this.loggedUser = this.appContext.getLoggedUser();
		this.searchForDocumentsMissingAcceptance();
	}

	private searchForDocumentsMissingAcceptance(): void {
		this.isPerformingRequest = true;

		this.userApiService.searchMissingAcceptanceOfDocuments(this.loggedUser?.Username!).subscribe({
			next: (operationResult: OperationResult<SearchMissingAcceptanceOfDocumentsResult>) => {
				this.isPerformingRequest = false;
				if (operationResult.IsValid) {
					this.processValidSearchMissingAcceptanceOfDocumentsResult(operationResult);
				} else {
					this.processInvalidResult(operationResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.logger.error('[MissingAcceptedComponent::searchMissingAcceptanceOfDocuments] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showError(
					'[MissingAcceptedComponent::searchMissingAcceptanceOfDocuments] HttpErrorResponse: ' +
						httpErrorResponse.error +
						httpErrorResponse.message
				);
			},
		});
	}

	private processValidSearchMissingAcceptanceOfDocumentsResult(
		operationResult: OperationResult<SearchMissingAcceptanceOfDocumentsResult>
	): void {
		let missingDocuments: SearchedMissingDocument[] | null = this.utilitiesService.ObjectDeepCopy(operationResult.Data.MissingDocuments);
		let missingDocumentLinks: SearchedMissingDocumentLink[] | null = this.utilitiesService.ObjectDeepCopy(
			operationResult.Data.MissingDocumentLinks
		);

		missingDocuments?.forEach((missingDoc) => {
			this.missingAcceptanceDocuments.push(<MissingAcceptedDocumentViewModel>{
				LegalDocumentType: missingDoc.LegalDocumentType,
				DocumentBlob: this.utilitiesService.base64ToBlob(missingDoc.Document, missingDoc.ContentType),
				ContentType: missingDoc.ContentType,
				DocumentId: missingDoc.DocumentId,
				AcceptanceHash: missingDoc.AcceptanceHash,
				Accepted: false,
				DocumentDownloadName: missingDoc.LegalDocumentType + '_name',
			});
		});

		missingDocumentLinks?.forEach((missingDoc) => {
			this.missingAcceptanceDocuments.push(<MissingAcceptedDocumentViewModel>{
				LegalDocumentType: missingDoc.LegalDocumentType,
				DocumentLink: missingDoc.DocumentLink,
				DocumentId: missingDoc.DocumentId,
				Accepted: false,
				DocumentDownloadName: missingDoc.LegalDocumentType + '_name',
			});
		});

		if (this.missingAcceptanceDocuments.length > 0) {
			this.missingAcceptanceDocuments.sort(function (doc1: MissingAcceptedDocumentViewModel, doc2: MissingAcceptedDocumentViewModel) {
				var doc1Code = doc1.LegalDocumentType.toUpperCase();
				var doc2Code = doc2.LegalDocumentType.toUpperCase();
				return doc1Code < doc2Code ? -1 : doc1Code > doc2Code ? 1 : 0;
			});
		}

		this.allDocumentsAccepted();
	}

	private isDocumentLinkType(missingDoc: MissingAcceptedDocumentViewModel) {
		return !!missingDoc && !!missingDoc.DocumentLink && !missingDoc.DocumentBlob && !missingDoc.ContentType;
	}

	public openLegalDocument(missingDoc: MissingAcceptedDocumentViewModel) {
		if (!missingDoc) {
			return;
		}

		if (this.isDocumentLinkType(missingDoc)) {
			window.open(missingDoc.DocumentLink, '_blank');
		} else {
			this.utilitiesService.openBlobNewWindowWithName(missingDoc.DocumentBlob as Blob, missingDoc.DocumentDownloadName, false);
		}
	}

	public allDocumentsAccepted(): void {
		this.allDocsAccepted = this.missingAcceptanceDocuments?.length > 0 && !this.missingAcceptanceDocuments.some((d) => !d.Accepted);
	}

	public refuseDocuments(): void {
		this.appContext.removeSession();
		this.router.navigateByUrl('/public/login');
	}

	public acceptDocuments(): void {
		let missingDocuments: AcceptMissingDocument[] = [];
		let missingDocumentLinks: AcceptMissingDocumentLink[] = [];

		this.missingAcceptanceDocuments.forEach((d: MissingAcceptedDocumentViewModel) => {
			if (this.isDocumentLinkType(d)) {
				missingDocumentLinks.push(<AcceptMissingDocumentLink>{
					LegalDocumentType: d.LegalDocumentType,
					DocumentId: d.DocumentId,
				});
			} else {
				missingDocuments.push(<AcceptMissingDocument>{
					LegalDocumentType: d.LegalDocumentType,
					DocumentId: d.DocumentId,
					AcceptanceHash: d.AcceptanceHash,
				});
			}
		});

		let acceptanceOfMissingDocuments: AcceptanceOfMissingDocumentsRequest = <AcceptanceOfMissingDocumentsRequest>{
			Username: this.loggedUser?.Username,
			AcceptanceMissingDocuments: missingDocuments,
			AcceptanceMissingDocumentLinks: missingDocumentLinks,
		};

		this.isPerformingRequest = true;

		this.userApiService.acceptMissingDocuments(acceptanceOfMissingDocuments).subscribe({
			next: (operationResult: OperationResult<boolean>) => {
				this.isPerformingRequest = false;
				if (operationResult.IsValid) {
					this.processValidAcceptMissingDocumentsResult(operationResult);
				} else {
					this.processInvalidResult(operationResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.logger.error('[MissingAcceptedComponent::searchMissingAcceptanceOfDocuments] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showError(
					'[MissingAcceptedComponent::searchMissingAcceptanceOfDocuments] HttpErrorResponse: ' +
						httpErrorResponse.error +
						httpErrorResponse.message
				);
			},
		});
	}

	private processValidAcceptMissingDocumentsResult(operationResult: OperationResult<boolean>): void {
		if (operationResult.IsValid && !!this.loggedUser) {
			let loggedUserData: EifLoggedUser | null = this.appContext.getLoggedUserData(this.loggedUser.Username!);

			if (!!loggedUserData && !!loggedUserData.Profiles && !!loggedUserData.SelectedProfile) {
				this.router.navigateByUrl('/private/home');
				return;
			}
		}

		this.appContext.removeSession();
		this.router.navigateByUrl('/public/login');
	}

	private processInvalidResult<T>(operationResult: OperationResult<T>): void {
		const errors = this.utilitiesService.GetDistinctOperationErrors(operationResult.Errors);
		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}
	}

	public getAdditionalDocumentText(legalDocumentType: string): string | null {
		if (legalDocumentType === LegalDocumentType.PersonalDataConsent) {
			return this.translateService.translateByLanguage(
				'missingAcceptanceDocument.contextInformation3',
				this.loggedUser?.LanguageCode,
				'client'
			);
		}

		return null;
	}

	public navigateToHome(): void {
		this.router.navigateByUrl('/private/home');
	}
}
